'use client';

import React, { useEffect, useRef, useState } from 'react';
import AppImage from '@/components/ui/AppImage';

const stats = [
  { value: 100, suffix: '%', label: 'Zadowolonych Klientów', desc: 'Na podstawie 14 recenzji Facebook' },
  { value: 5, suffix: '★', label: 'Ocena na Facebook', desc: 'Wszystkie opinie 5-gwiazdkowe' },
  { value: 7, suffix: '+', label: 'Usług Detailingowych', desc: 'Od mycia po ceramikę' },
  { value: 579, suffix: '', label: 'Obserwujących', desc: 'Społeczność na Facebook' },
];

const reasons = [
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    ),
    title: 'Profesjonalny sprzęt',
    desc: 'Używamy wyłącznie certyfikowanych środków i profesjonalnych maszyn polerskich.',
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3" /><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14" />
      </svg>
    ),
    title: 'Indywidualne podejście',
    desc: 'Każde auto traktujemy indywidualnie — dobieramy metody do stanu i potrzeb pojazdu.',
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
    title: 'Lokalna firma, osobisty kontakt',
    desc: 'Działamy w Sieprawiu — jesteśmy dostępni, znamy naszych klientów osobiście.',
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    ),
    title: 'Gwarantowana jakość',
    desc: 'Nie oddajemy auta dopóki nie jesteśmy w 100% zadowoleni z efektu.',
  },
];

function useCountUp(target: number, duration: number, active: boolean) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!active) return;
    let start = 0;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration, active]);
  return count;
}

function StatCard({ stat, active }: { stat: typeof stats[0]; active: boolean }) {
  const count = useCountUp(stat.value, 1200, active);
  return (
    <div className="service-card rounded-2xl p-6 text-center group hover:border-kd-cyan/40 transition-all">
      <div className="stat-number text-5xl md:text-6xl mb-1">
        {count}{stat.suffix}
      </div>
      <div className="font-heading font-bold text-white text-lg uppercase tracking-wide mb-1">
        {stat.label}
      </div>
      <div className="text-kd-muted text-xs font-body">{stat.desc}</div>
    </div>
  );
}

export default function WhyUsSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            setActive(true);
          }
        });
      },
      { threshold: 0.15 }
    );
    const els = sectionRef.current?.querySelectorAll('.reveal-hidden, .reveal-scale');
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="dlaczego-my" className="py-24 md:py-32 bg-kd-black relative overflow-hidden">
      {/* Decorative glow */}
      <div className="absolute right-0 top-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-gradient-radial-cyan blur-3xl pointer-events-none opacity-40" />

      <div className="max-w-container mx-auto px-6 relative z-10">
        {/* Section header */}
        <div className="mb-16 reveal-hidden">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px w-12 bg-kd-cyan" />
            <span className="text-kd-cyan text-xs font-body font-semibold uppercase tracking-widest">
              Dlaczego My
            </span>
          </div>
          <h2 className="font-heading font-black uppercase text-white" style={{ fontSize: 'clamp(2.5rem, 6vw, 5.5rem)', lineHeight: '0.95' }}>
            ZAUFAJ<br />
            <span className="gradient-text">EKSPERTOM</span>
          </h2>
        </div>

        {/* Layout: image left + reasons right */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          {/* Image */}
          <div className="reveal-scale relative">
            <div className="relative rounded-2xl overflow-hidden aspect-[4/3] glow-cyan">
              <AppImage
                src="https://images.pexels.com/photos/6873087/pexels-photo-6873087.jpeg"
                alt="Technik w czarnych rękawiczkach polerujący lakier samochodu maszyną polerską"
                fill
                className="object-cover"
              />
              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-kd-black/60 via-transparent to-transparent" />
              {/* Floating badge */}
              <div className="absolute bottom-6 left-6 glass-card rounded-xl px-4 py-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-kd-cyan/20 flex items-center justify-center">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#3AA4DE" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-heading font-bold text-white text-sm uppercase">100% Rekomendacji</p>
                    <p className="text-kd-muted text-xs font-body">Facebook · 14 recenzji</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Reasons list */}
          <div className="space-y-5">
            {reasons.map((r, i) => (
              <div
                key={r.title}
                className="reveal-hidden flex gap-4 items-start p-5 service-card rounded-xl group"
                style={{ transitionDelay: `${i * 0.1}s` }}
              >
                <div className="shrink-0 w-10 h-10 rounded-xl bg-kd-cyan/10 flex items-center justify-center text-kd-cyan group-hover:bg-kd-cyan/20 transition-colors">
                  {r.icon}
                </div>
                <div>
                  <h3 className="font-heading font-bold text-white text-lg uppercase tracking-wide mb-1">
                    {r.title}
                  </h3>
                  <p className="text-kd-muted font-body text-sm leading-relaxed">{r.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 reveal-hidden">
          {stats.map((s) => (
            <StatCard key={s.label} stat={s} active={active} />
          ))}
        </div>
      </div>
    </section>
  );
}